import { produce, Draft } from 'immer'
import { IAction, TActionTypes } from '@Root/types'
import Cookies from 'universal-cookie'
import map from 'lodash/map'
import find from 'lodash/find'
import filter from 'lodash/filter'
import without from 'lodash/without'
import difference from 'lodash/difference'
import smartMerge from '@Utils/SmartMerge'

import {
  ILabel,
  ILabelState,
  TLabelActionTypes,
  LabelActionTypes,
  IChangeSelectedLabelsPayload,
  IFetchLabelsSuccessResponseBody,
  IRenameLabelSuccessPayload,
  IDeleteLabelSuccessPayload,
  ICreateProjectLabelsSuccessPayload,
  IDeleteProjectLabelsSuccessPayload,
  ICreateLabelSuccessResponseBody,
  ISetLabelSelectionPayload,
} from './types'

import {
  WorkspaceActionTypes
} from '@SiteModules/Workspace/types'

import {
  TProjectActionTypes,
  ProjectActionTypes,
  IDeleteProjectsSuccessPayload,
} from '@SiteModules/Project/types'

export const initialState: ILabelState = {
  labels: [],
  selectedLabelIds: [],
  hasLoadedInitially: false,
  loading: false,
}

type TAction = TActionTypes | TLabelActionTypes | TProjectActionTypes

export const labelsReducer = (
  state: ILabelState = initialState,
  action: TAction
): ILabelState => {
  return produce(state, draft => {
    switch (action.type) {
      case WorkspaceActionTypes.SET_CURRENT_WORKSPACE:
      case WorkspaceActionTypes.RESET_CURRENT_WORKSPACE:
        resetLabels(draft)
        break

      case LabelActionTypes.FETCH_LABELS:
        fetchLabels(draft)
        break

      case LabelActionTypes.FETCH_LABELS_SUCCESS:
        fetchLabelsSuccess(draft, action)
        break

      case LabelActionTypes.FETCH_LABELS_FAILURE:
        fetchLabelsFailure(draft)
        break

      case LabelActionTypes.CREATE_LABEL:
      case LabelActionTypes.RENAME_LABEL:
      case LabelActionTypes.DELETE_LABEL:
        startLoading(draft)
        break

      case LabelActionTypes.CREATE_LABEL_FAILURE:
      case LabelActionTypes.RENAME_LABEL_FAILURE:
      case LabelActionTypes.DELETE_LABEL_FAILURE:
        endLoading(draft)
        break

      case LabelActionTypes.CREATE_LABEL_SUCCESS:
        createLabelSuccess(draft, action)
        break

      case LabelActionTypes.RENAME_LABEL_SUCCESS:
        renameLabelSuccess(draft, action)
        break

      case LabelActionTypes.DELETE_LABEL_SUCCESS:
        deleteLabelSuccess(draft, action)
        break

      case LabelActionTypes.CHANGE_SELECTED_LABELS:
        changeSelectedLabels(draft, action)
        break

      case LabelActionTypes.SET_LABEL_SELECTION:
        setLabelSelection(draft, action)
        break

      case LabelActionTypes.CREATE_PROJECT_LABELS_SUCCESS:
        createProjectLabelsSuccess(draft, action)
        break

      case LabelActionTypes.DELETE_PROJECT_LABELS_SUCCESS:
        deleteProjectLabelsSuccess(draft, action)
        break

      case ProjectActionTypes.DELETE_PROJECTS_SUCCESS:
        updateLabelsAfterDeletingProjects(draft, action)
        break

      default:
        break
    }
  })
}

const resetLabels = (draft: Draft<ILabelState>) => {
  draft.hasLoadedInitially = false
  draft.labels = []
  draft.selectedLabelIds = []
}

const fetchLabels = (draft: Draft<ILabelState>) => {
  draft.loading = true
}

function isIFetchLabelsSuccessAction(arg: any): arg is IAction<IFetchLabelsSuccessResponseBody> {
  return arg.type === LabelActionTypes.FETCH_LABELS_SUCCESS
}

const fetchLabelsSuccess = (draft: Draft<ILabelState>, action: TAction) => {
  if (!isIFetchLabelsSuccessAction(action)) { return }

  draft.labels = map(action.payload.data, data => {
    return ({
      id: parseInt(data.id, 10),
      attributes: data.attributes
    })
  })

  draft.hasLoadedInitially = true
  draft.loading = false
}

const fetchLabelsFailure = (draft: Draft<ILabelState>) => {
  draft.loading = false
}

const startLoading = (draft: Draft<ILabelState>) => {
  draft.loading = true
}

const endLoading = (draft: Draft<ILabelState>) => {
  draft.loading = false
}

function isICreateLabelSuccessAction(arg: any): arg is IAction<ICreateLabelSuccessResponseBody> {
  return arg.type === LabelActionTypes.CREATE_LABEL_SUCCESS
}

const createLabelSuccess = (draft: Draft<ILabelState>, action: TAction) => {
  if (!isICreateLabelSuccessAction(action)) { return }

  const newLabel = {
    id: parseInt(action.payload.data.id, 10),
    attributes: action.payload.data.attributes
  }

  draft.labels = [newLabel].concat(draft.labels)
  draft.loading = false
}

function isIRenameLabelSuccessAction(arg: any): arg is IAction<IRenameLabelSuccessPayload> {
  return arg.type === LabelActionTypes.RENAME_LABEL_SUCCESS
}

const renameLabelSuccess = (draft: Draft<ILabelState>, action: TAction) => {
  if (!isIRenameLabelSuccessAction(action)) { return }

  let updatedLabel = find(draft.labels, label => label.id === action.payload.labelId) as ILabel
  const updatedLabelAttributes = { ...updatedLabel.attributes, caption: action.payload.caption }
  updatedLabel = { ...updatedLabel, attributes: updatedLabelAttributes }

  draft.labels = smartMerge(draft.labels, [updatedLabel])
  draft.loading = false
}

function isIDeleteLabelSuccessAction(arg: any): arg is IAction<IDeleteLabelSuccessPayload> {
  return arg.type === LabelActionTypes.DELETE_LABEL_SUCCESS
}

const deleteLabelSuccess = (draft: Draft<ILabelState>, action: TAction) => {
  if (!isIDeleteLabelSuccessAction(action)) { return }

  draft.labels = filter(draft.labels, label => label.id !== action.payload.labelId)
  draft.loading = false
}

function isIChangeSelectedLabelsAction(arg: any): arg is IAction<IChangeSelectedLabelsPayload> {
  return arg.type === LabelActionTypes.CHANGE_SELECTED_LABELS
}

const changeSelectedLabels = (draft: Draft<ILabelState>, action: TAction) => {
  if (!isIChangeSelectedLabelsAction(action)) { return }

  const { selectLabel, labelId } = action.payload

  if (selectLabel) {
    draft.selectedLabelIds.push(labelId)
  } else {
    draft.selectedLabelIds = without(draft.selectedLabelIds, labelId)
  }

  const cookies = new Cookies()
  cookies.set('selectedLabelIds', draft.selectedLabelIds, { path: '/' })
}

function isISetLabelSelectionAction(arg: any): arg is IAction<ISetLabelSelectionPayload> {
  return arg.type === LabelActionTypes.SET_LABEL_SELECTION
}

const setLabelSelection = (draft: Draft<ILabelState>, action: TAction) => {
  if (!isISetLabelSelectionAction(action)) { return }

  draft.selectedLabelIds = action.payload.labelIds

  const cookies = new Cookies()
  cookies.set('selectedLabelIds', draft.selectedLabelIds, { path: '/' })
}

function isICreateProjectLabelsSuccessAction(arg: any): arg is IAction<ICreateProjectLabelsSuccessPayload> {
  return arg.type === LabelActionTypes.CREATE_PROJECT_LABELS_SUCCESS
}

const createProjectLabelsSuccess = (draft: Draft<ILabelState>, action: TAction) => {
  if (!isICreateProjectLabelsSuccessAction(action)) { return }

  let updatedLabel = find(draft.labels, label => label.id === action.payload.labelId) as ILabel
  const updatedLabelAttributes = { ...updatedLabel.attributes, projectIds: updatedLabel.attributes.projectIds.concat(action.payload.selectedProjectIds) }
  updatedLabel = { ...updatedLabel, attributes: updatedLabelAttributes }

  draft.labels = smartMerge(draft.labels, [updatedLabel])
}

function isIDeleteProjectLabelsSuccessAction(arg: any): arg is IAction<IDeleteProjectLabelsSuccessPayload> {
  return arg.type === LabelActionTypes.DELETE_PROJECT_LABELS_SUCCESS
}

const deleteProjectLabelsSuccess = (draft: Draft<ILabelState>, action: TAction) => {
  if (!isIDeleteProjectLabelsSuccessAction(action)) { return }

  let updatedLabel = find(draft.labels, label => label.id === action.payload.labelId) as ILabel
  const updatedLabelAttributes = { ...updatedLabel.attributes, projectIds: difference(updatedLabel.attributes.projectIds, action.payload.selectedProjectIds) }
  updatedLabel = { ...updatedLabel, attributes: updatedLabelAttributes }

  draft.labels = smartMerge(draft.labels, [updatedLabel])
}

function isIDeleteProjectsSuccessAction(arg: any): arg is IAction<IDeleteProjectsSuccessPayload> {
  return arg.type === ProjectActionTypes.DELETE_PROJECTS_SUCCESS
}

const updateLabelsAfterDeletingProjects = (draft: Draft<ILabelState>, action: TAction) => {
  if (!isIDeleteProjectsSuccessAction(action)) { return }

  draft.labels = map(draft.labels, label => {
    const updatedProjectIds = filter(label.attributes.projectIds, projectId => !action.payload.projectIds.includes(projectId))

    return ({ ...label, attributes: { ...label.attributes, projectIds: updatedProjectIds} })
  })
}
